import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, LayoutGrid, Table2, Flame, UserPlus, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageTitle } from "./DashboardLayout";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

const STATUSES = ["New", "Reviewing", "Contacted", "Consultation Scheduled", "Estimate Requested", "Estimate Sent", "Negotiating", "Won", "Lost", "No Response", "Spam"];
const TEMPS = ["hot", "warm", "informational", "disqualified", "spam"];
const TEMP_COLOR = { hot: "bg-red-100 text-red-700", warm: "bg-amber-100 text-amber-700", informational: "bg-blue-100 text-blue-700", disqualified: "bg-slate-100 text-slate-500", spam: "bg-slate-100 text-slate-400" };

export default function Leads() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canDelete = ["owner", "admin"].includes(user?.role);
  const [view, setView] = useState("table");
  const [open, setOpen] = useState(false);
  const { data: leads } = useQuery({ queryKey: ["leads"], queryFn: () => api.get("/leads").then(r => r.data.leads) });

  const update = async (id, field, value) => {
    try { await api.put(`/leads/${id}`, { [field]: value }); qc.invalidateQueries({ queryKey: ["leads"] }); qc.invalidateQueries({ queryKey: ["clients"] }); toast.success("Lead updated"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const convert = async (lead) => {
    try {
      await api.post(`/leads/${lead.id}/convert`);
      qc.invalidateQueries({ queryKey: ["leads"] });
      qc.invalidateQueries({ queryKey: ["clients"] });
      toast.success(`${lead.firstName} is now a client`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    await api.delete(`/leads/${id}`);
    qc.invalidateQueries({ queryKey: ["leads"] });
    toast.success("Lead deleted");
  };

  return (
    <div>
      <PageTitle title="Leads" subtitle="Manage your sales pipeline" action={
        <div className="flex gap-2">
          <div className="flex rounded-lg border border-slate-200 overflow-hidden">
            <button onClick={() => setView("table")} className={cn("p-2", view === "table" ? "bg-electric text-white" : "bg-white text-slate-500")} data-testid="leads-view-table"><Table2 className="h-4 w-4" /></button>
            <button onClick={() => setView("kanban")} className={cn("p-2", view === "kanban" ? "bg-electric text-white" : "bg-white text-slate-500")} data-testid="leads-view-kanban"><LayoutGrid className="h-4 w-4" /></button>
          </div>
          <NewLeadDialog open={open} setOpen={setOpen} onCreated={() => qc.invalidateQueries({ queryKey: ["leads"] })} />
        </div>
      } />

      {view === "table" ? (
        <div className="rounded-xl bg-white border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="leads-table">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase font-mono">
                <tr><th className="text-left p-3">Name</th><th className="text-left p-3">Contact</th><th className="text-left p-3">Category</th><th className="text-left p-3">Source</th><th className="text-left p-3">Temp</th><th className="text-left p-3">Status</th><th className="text-left p-3">Actions</th></tr>
              </thead>
              <tbody>
                {(leads || []).length === 0 ? <tr><td colSpan={7} className="p-8 text-center text-slate-400">No leads yet.</td></tr> :
                  (leads || []).map((l) => (
                    <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`lead-row-${l.id}`}>
                      <td className="p-3 font-medium text-navy">{l.firstName} {l.lastName}{l.businessName && <span className="block text-xs text-slate-400">{l.businessName}</span>}</td>
                      <td className="p-3 text-slate-600">{l.email || l.phone || "—"}</td>
                      <td className="p-3 text-slate-600">{l.serviceCategory || "—"}</td>
                      <td className="p-3"><span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600">{l.source}</span></td>
                      <td className="p-3">
                        <select value={l.temperature || "informational"} onChange={e => update(l.id, "temperature", e.target.value)} className={cn("text-xs rounded-full px-2 py-1 border-0 cursor-pointer", TEMP_COLOR[l.temperature] || TEMP_COLOR.informational)} data-testid={`lead-temp-${l.id}`}>
                          {TEMPS.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </td>
                      <td className="p-3">
                        <select value={l.status} onChange={e => update(l.id, "status", e.target.value)} className="text-xs rounded-md border border-slate-200 px-2 py-1 cursor-pointer" data-testid={`lead-status-${l.id}`}>
                          {STATUSES.map(s => <option key={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="p-3"><div className="flex items-center gap-2">
                        {l.convertedToClient ? (
                          <span className="inline-flex items-center gap-1 text-xs text-emerald-600" data-testid={`lead-client-badge-${l.id}`}><CheckCircle2 className="h-3.5 w-3.5" /> Client</span>
                        ) : l.email ? (
                          <button onClick={() => convert(l)} title="Convert to client" data-testid={`lead-convert-${l.id}`} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-electric/10 text-electric hover:bg-electric hover:text-white transition-colors"><UserPlus className="h-3.5 w-3.5" /> Convert</button>
                        ) : null}
                        {canDelete && <ConfirmDelete onConfirm={() => remove(l.id)} title="Delete this lead?" description={`${l.firstName} ${l.lastName || ""} will be removed from your pipeline.`} testid={`lead-delete-${l.id}`} />}
                      </div></td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-4" data-testid="leads-kanban">
          {STATUSES.slice(0, 8).map((s) => {
            const items = (leads || []).filter(l => l.status === s);
            return (
              <div key={s} className="w-64 shrink-0">
                <div className="flex items-center justify-between mb-2"><h4 className="font-heading font-semibold text-sm text-navy">{s}</h4><span className="text-xs text-slate-400">{items.length}</span></div>
                <div className="space-y-2">
                  {items.map((l) => (
                    <div key={l.id} className="rounded-lg bg-white border border-slate-200 p-3">
                      <div className="flex items-center justify-between"><p className="text-sm font-medium text-navy">{l.firstName} {l.lastName}</p><div className="flex items-center gap-1">{l.temperature === "hot" && <Flame className="h-3.5 w-3.5 text-red-500" />}{l.convertedToClient ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> : l.email && <button onClick={() => convert(l)} title="Convert to client" data-testid={`lead-convert-kanban-${l.id}`} className="text-electric hover:text-tech transition-colors"><UserPlus className="h-3.5 w-3.5" /></button>}{canDelete && <ConfirmDelete onConfirm={() => remove(l.id)} title="Delete this lead?" description={`${l.firstName} ${l.lastName || ""} will be removed.`} testid={`lead-delete-kanban-${l.id}`} />}</div></div>
                      <p className="text-xs text-slate-500 mt-0.5">{l.serviceCategory}</p>
                      <select value={l.status} onChange={e => update(l.id, "status", e.target.value)} className="mt-2 w-full text-xs rounded border border-slate-200 px-1.5 py-1">
                        {STATUSES.map(st => <option key={st}>{st}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function NewLeadDialog({ open, setOpen, onCreated }) {
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", phone: "", businessName: "", serviceCategory: "", summary: "" });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const input = "w-full h-10 px-3 rounded-lg border border-input text-sm focus:outline-none focus:ring-2 focus:ring-electric";
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/leads", form); toast.success("Lead created"); setOpen(false); setForm({ firstName: "", lastName: "", email: "", phone: "", businessName: "", serviceCategory: "", summary: "" }); onCreated(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="bg-electric hover:bg-tech gap-1.5" data-testid="new-lead-btn"><Plus className="h-4 w-4" /> New Lead</Button></DialogTrigger>
      <DialogContent><DialogHeader><DialogTitle>New Lead</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input className={input} placeholder="First name *" required value={form.firstName} onChange={e => set("firstName", e.target.value)} data-testid="lead-first" />
            <input className={input} placeholder="Last name" value={form.lastName} onChange={e => set("lastName", e.target.value)} data-testid="lead-last" />
            <input className={input} placeholder="Email" value={form.email} onChange={e => set("email", e.target.value)} data-testid="lead-email" />
            <input className={input} placeholder="Phone" value={form.phone} onChange={e => set("phone", e.target.value)} data-testid="lead-phone" />
          </div>
          <input className={input} placeholder="Business name" value={form.businessName} onChange={e => set("businessName", e.target.value)} />
          <input className={input} placeholder="Service category" value={form.serviceCategory} onChange={e => set("serviceCategory", e.target.value)} />
          <textarea className={`${input} h-auto py-2`} rows={2} placeholder="Summary" value={form.summary} onChange={e => set("summary", e.target.value)} />
          <Button type="submit" className="w-full bg-electric hover:bg-tech" data-testid="lead-save">Create Lead</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
